/**
 * Brand → PrimeVue preset adapter (Phase 6 plumbing — UI integration is
 * deferred until the AssistantEditor / CitationPopup / etc. components land).
 *
 * Reads the 11 colour + 3 radius tokens off `brand.json` (see brand.ts)
 * and produces a PrimeVue `definePreset` argument shape with `primitive`
 * + `semantic` token slices for both light and dark colour schemes.
 *
 * Why a separate adapter file?
 *   - brand.ts must stay zero-dependency so the Python contract test in
 *     `tests/test_brand_config.py` can still read it without parsing TS
 *     imports.
 *   - The PrimeVue preset shape is opinionated (50/100/.../950 scale per
 *     colour); converting from our flat 11-token brand JSON to PrimeVue's
 *     graduated scale is a one-time mapping that belongs adjacent to the
 *     UI layer, not the brand-loading core.
 *
 * Usage in a future App.vue (NOT yet wired — needs browser verification):
 *
 *     import { definePreset } from "@primevue/themes";
 *     import Lara from "@primevue/themes/lara";
 *     import { brandToPrimevuePreset } from "./theme/brand-to-primevue";
 *     const preset = definePreset(Lara, brandToPrimevuePreset(brand));
 *     app.use(PrimeVue, { theme: { preset } });
 *
 * Test surface lives in `tests/test_brand_to_primevue.py` — Python because
 * this repo's web tests are pytest-driven (see tests/test_brand_config.py).
 */

import type { BrandConfig, BrandTheme } from "../brand";

/**
 * The minimum shape definePreset() needs from us — partial preset overlay
 * against a PrimeVue base theme (Lara, Aura, etc.). All fields optional;
 * unset slices fall through to the base preset.
 */
export interface PrimevuePresetOverlay {
  semantic?: {
    primary?: ColorScale;
    colorScheme?: {
      light?: SemanticScheme;
      dark?: SemanticScheme;
    };
  };
  /** Token bag for non-semantic primitives (radius, etc.). */
  primitive?: Record<string, string>;
}

interface SemanticScheme {
  surface?: ColorScale;
  primary?: { color?: string; contrastColor?: string; hoverColor?: string };
  text?: { color?: string; mutedColor?: string };
  formField?: { borderColor?: string };
}

interface ColorScale {
  50?: string;
  100?: string;
  200?: string;
  300?: string;
  400?: string;
  500?: string;
  600?: string;
  700?: string;
  800?: string;
  900?: string;
  950?: string;
}

const RGB_HEX = /^#([0-9a-f]{6})$/i;

/** Best-effort hex → relative luminance (0..1). Non-hex returns 0.5. */
function luminance(hex: string): number {
  const m = RGB_HEX.exec(hex.trim());
  if (!m) return 0.5;
  const c = m[1];
  const r = parseInt(c.slice(0, 2), 16) / 255;
  const g = parseInt(c.slice(2, 4), 16) / 255;
  const b = parseInt(c.slice(4, 6), 16) / 255;
  // Standard sRGB → relative luminance.
  const lin = (x: number) =>
    x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/**
 * Derive a dark-mode `BrandTheme` from a light-mode one.
 *
 * Strategy:
 *   - colour_bg, colour_surface, colour_surface_alt: keep the *darker*
 *     side of the gradient (flip if the source is light).
 *   - colour_ink: flip to a light text colour.
 *   - colour_muted: dimmer of the same hue.
 *   - colour_accent / accentHover / accentDim: preserve (the brand is
 *     the brand).
 *   - colour_border: dim grey.
 *   - radius: identical (radii don't change per scheme).
 *
 * Operators wanting tighter control set `themeDark` on brand.json — that
 * wins over derivation.
 */
export function deriveDarkTheme(light: BrandTheme): BrandTheme {
  // If the operator already gave us a bg, use it; otherwise pick a sane dark.
  const bgLum = light.colorBg ? luminance(light.colorBg) : 1;
  const isLightSource = bgLum > 0.5;
  const dark: BrandTheme = {
    colorBg: isLightSource ? "#0f1115" : light.colorBg ?? "#0f1115",
    colorSurface: isLightSource ? "#161922" : light.colorSurface ?? "#161922",
    colorSurfaceAlt: isLightSource
      ? "#1d2230"
      : light.colorSurfaceAlt ?? "#1d2230",
    colorBorder: "#2a2f3d",
    colorInk: "#e8eaf0",
    colorMuted: "#8a90a3",
    colorAccent: light.colorAccent,
    colorAccentHover: light.colorAccentHover,
    colorAccentDim: light.colorAccentDim,
    // The interactive accent carries through to dark unchanged: it is chosen
    // to stand out against BOTH the light surface and the primary, which is
    // exactly what a dark scheme needs from it too.
    colorHighlight: light.colorHighlight,
    colorHighlightHover: light.colorHighlightHover,
    colorSuccess: light.colorSuccess,
    colorError: light.colorError,
    radiusSm: light.radiusSm,
    radiusMd: light.radiusMd,
    radiusLg: light.radiusLg,
  };
  return dark;
}

/**
 * Translate one BrandTheme into a PrimeVue SemanticScheme slice. PrimeVue's
 * preset system uses graduated 50..950 scales for the surface tone; we map
 * the three brand surfaces (bg / surface / surfaceAlt) onto 50/100/200 in
 * light mode and 900/800/700 in dark mode so existing PrimeVue components
 * pick reasonable defaults.
 */
function themeToScheme(t: BrandTheme, mode: "light" | "dark"): SemanticScheme {
  const surface: ColorScale =
    mode === "light"
      ? { 50: t.colorBg, 100: t.colorSurface, 200: t.colorSurfaceAlt }
      : { 900: t.colorBg, 800: t.colorSurface, 700: t.colorSurfaceAlt };
  return {
    surface,
    primary: {
      color: t.colorAccent,
      hoverColor: t.colorAccentHover,
      // Contrast colour for text on a primary background. We don't have
      // it in brand.json today — most palettes pair an accent with white;
      // operators wanting a different value set it explicitly via theme
      // overrides in a future iteration.
      contrastColor: "#ffffff",
    },
    text: {
      color: t.colorInk,
      mutedColor: t.colorMuted,
    },
    formField: {
      borderColor: t.colorBorder,
    },
  };
}

/**
 * Produce a complete preset overlay for `definePreset(...)`.
 *
 * Skips any scheme whose source theme is empty (no overrides) so the
 * underlying PrimeVue base preset shows through. This keeps brand.json
 * fully optional — a deployment with no `theme` block still gets the
 * default Lara/Aura look.
 */
export function brandToPrimevuePreset(brand: BrandConfig): PrimevuePresetOverlay {
  const overlay: PrimevuePresetOverlay = {};
  const lightHasTokens = Object.keys(brand.theme ?? {}).length > 0;
  const darkSource =
    brand.themeDark && Object.keys(brand.themeDark).length > 0
      ? brand.themeDark
      : lightHasTokens
        ? deriveDarkTheme(brand.theme)
        : undefined;

  if (!lightHasTokens && !darkSource) return overlay;

  const semantic: PrimevuePresetOverlay["semantic"] = {};
  if (lightHasTokens || darkSource) {
    semantic.colorScheme = {};
  }
  if (lightHasTokens && semantic.colorScheme) {
    semantic.colorScheme.light = themeToScheme(brand.theme, "light");
  }
  if (darkSource && semantic.colorScheme) {
    semantic.colorScheme.dark = themeToScheme(darkSource, "dark");
  }
  // Surface a single `primary` at the top-level too — PrimeVue components
  // that don't honour the per-scheme palette read this as the universal
  // accent.
  if (brand.theme.colorAccent) {
    semantic.primary = {
      500: brand.theme.colorAccent,
      600: brand.theme.colorAccentHover ?? brand.theme.colorAccent,
    };
  }
  overlay.semantic = semantic;

  const primitive: Record<string, string> = {};
  if (brand.theme.radiusSm) primitive["borderRadius.sm"] = brand.theme.radiusSm;
  if (brand.theme.radiusMd) primitive["borderRadius.md"] = brand.theme.radiusMd;
  if (brand.theme.radiusLg) primitive["borderRadius.lg"] = brand.theme.radiusLg;
  if (Object.keys(primitive).length > 0) overlay.primitive = primitive;

  return overlay;
}
